import React from "react";

import { IgTooltip } from "@intelligems/ig-theme";
import { Box } from "@mui/material";

import { EXPERIENCE_TYPE_ICONS } from "./experienceTypeIconsMap";

export const getExperienceIcon = (
  experience: any,
  size: number = 20,
  color?: string
): React.ReactElement | null => {
  // 1. Offer experiences
  const offer = experience?.variations?.[0]?.offer;
  if (offer?.discountApplicationType) {
    let iconKey: string;
    if (offer.discountApplicationType === "cartDiscount") {
      iconKey =
        offer.discountApplicationMethod === "perItem"
          ? "cartDiscount_perItem"
          : "cartDiscount_perOrder";
    } else {
      iconKey = offer.discountApplicationType;
    }
    const Icon = EXPERIENCE_TYPE_ICONS[iconKey];
    if (Icon) return <Icon size={size} color={color} />;
  }

  // 2. Site content and checkout block experiences
  const testTypes = experience?.testTypes;
  if (testTypes) {
    if (testTypes.hasTestOnsiteUpsell) {
      const Icon = EXPERIENCE_TYPE_ICONS["onsiteUpsell"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestCheckoutBlocks) {
      const Icon = EXPERIENCE_TYPE_ICONS["checkoutBlock"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestPostPurchase) {
      const Icon = EXPERIENCE_TYPE_ICONS["postPurchase"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestPricing) {
      const Icon = EXPERIENCE_TYPE_ICONS["pricing"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestContentUrl) {
      const Icon = EXPERIENCE_TYPE_ICONS["splitUrl"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestContentTemplate) {
      const Icon = EXPERIENCE_TYPE_ICONS["template"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestContentTheme) {
      const Icon = EXPERIENCE_TYPE_ICONS["theme"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestContentOnsite) {
      const Icon = EXPERIENCE_TYPE_ICONS["onsiteEdits"];
      if (Icon) return <Icon size={size} color={color} />;
    }
    if (testTypes.hasTestOnsiteInjections) {
      const Icon = EXPERIENCE_TYPE_ICONS["onsiteInjections"];
      if (Icon) return <Icon size={size} color={color} />;
    }
  }
  return null;
};

const getExperienceIconTooltipTitle = (experience: any): string => {
  // 1. Handle offer experiences - same logic as getExperienceIcon
  const offer = experience?.variations?.[0]?.offer;
  if (offer?.discountApplicationType) {
    if (offer.discountApplicationType === "cartDiscount") {
      return offer.discountApplicationMethod === "perItem"
        ? "Cart Discount (Per Item) Personalization"
        : "Cart Discount (Per Order) Personalization";
    }
    if (offer.discountApplicationType === "tieredDiscount") {
      return "Tiered Discount Personalization";
    }
    if (offer.discountApplicationType === "freeShipping") {
      return "Free Shipping Personalization";
    }
    if (offer.discountApplicationType === "freeGift") {
      return "Free Gift Personalization";
    }
  }

  // 2. Handle other experience types using testTypes - same logic as getExperienceIcon
  const testTypes = experience?.testTypes;
  if (testTypes) {
    if (testTypes.hasTestOnsiteUpsell) {
      return "Onsite Upsell Personalization";
    }
    if (testTypes.hasTestCheckoutBlocks) {
      return "Checkout Personalization";
    }
    if (testTypes.hasTestPostPurchase) {
      return "Post-Purchase Personalization";
    }
    if (testTypes.hasTestPricing) {
      return "Price Personalization";
    }
    if (testTypes.hasTestContentUrl) {
      return "URL Redirect Personalization";
    }
    if (testTypes.hasTestContentTemplate) {
      return "Template Personalization";
    }
    if (testTypes.hasTestContentTheme) {
      return "Theme Personalization";
    }
    if (testTypes.hasTestContentOnsite) {
      return "Content Edit Personalization";
    }
    if (testTypes.hasTestOnsiteInjections) {
      return "Styles & Javascript Personalization";
    }
  }

  return "Advanced Personalization";
};

export const getExperienceIconWithTooltip = (
  experience: any,
  size: number = 20,
  color?: string
): React.ReactElement | null => {
  const icon = getExperienceIcon(experience, size, color);

  if (!icon) return null;

  return (
    <IgTooltip title={getExperienceIconTooltipTitle(experience)}>
      <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
    </IgTooltip>
  );
};
